// The game's own symbols, and the characters the icon font draws them with.
//
// One table, two readers: the HUD swaps `[R]` in rules text for its glyph, and
// the deck picker prints a legend's domains as the coloured runes rather than
// as the words "fury" and "calm". A second copy of this mapping is exactly the
// kind of thing that drifts, and the drift is silent -- a wrong letter renders
// as a letter, not as an error.
//
// Deliberately free of the DOM, so `icons.test.ts` can import it and hold
// every glyph against the font's own cmap. It used to be a literal inside
// hud.ts and the test had to read that file as *text* and regex the table out,
// because importing the HUD means importing the DOM.

/**
 * The rules shorthand mapped onto Riftbound's icon font.
 *
 * Taken from riftcardmaker's own `TextSymbol` table, which is where the font
 * comes from -- not inferred from the letters. Inferring got two of them wrong:
 * "R" is the rainbow rune (POWER_COLORED) rather than Fury, and Calm is "c"
 * while "U" is the unit type icon.
 *
 * Case matters: "C" is Chaos and "c" is Calm. The font also carries type icons
 * (B battlefield, G gear, H champion, L legend, S spell, U unit) which card
 * text does not use.
 */
export const SYMBOLS: Record<string, { glyph: string; kind: string }> = {
  M: { glyph: 'M', kind: 'might' },
  E: { glyph: 'T', kind: 'exhaust' },
  R: { glyph: 'f', kind: 'fury' },
  G: { glyph: 'c', kind: 'calm' },
  B: { glyph: 'm', kind: 'mind' },
  O: { glyph: 'p', kind: 'body' },
  P: { glyph: 'C', kind: 'chaos' },
  Y: { glyph: 'o', kind: 'order' },
  /**
   * 204. One Power of any domain -- the rainbow rune, at "P".
   *
   * It *is* in the font, and finding that took two wrong turns. riftcardmaker's
   * table calls the symbol "R", which is not where its glyph lives: asking for
   * "R" printed a bare letter R through the cost line of the 240 cards that
   * use it. The conclusion drawn from that -- that the font had no glyph for it
   * and it had to be drawn in CSS -- was wrong as well. The cmap carries "P",
   * and nothing else was using it. A drawn approximation of a glyph the font
   * actually has is worse than either mistake, because it looks deliberate.
   */
  A: { glyph: 'P', kind: 'any' },
};

/**
 * A domain's shorthand, as rules text spells it.
 *
 * Card text writes the letter; a `CardDef` names the domain. The two meet
 * here, and only here -- guessing the letter from the name is what got Calm
 * and Fury wrong the first time, because `C` is Chaos and `c` is Calm.
 */
export const DOMAIN_SYMBOL: Record<string, string> = {
  fury: 'R',
  calm: 'G',
  mind: 'B',
  body: 'O',
  chaos: 'P',
  order: 'Y',
};

/** One symbol, as the markup the stylesheet expects. */
export function symbolHtml(token: string): string | null {
  const found = SYMBOLS[token];
  return found ? `<i class="sym sym-${found.kind}">${found.glyph}</i>` : null;
}

/**
 * A card's domains as their runes.
 *
 * Falls back to the word for anything the table does not know, so a domain
 * added to the game shows up as text rather than vanishing from the line.
 */
export function domainsHtml(domains: readonly string[]): string {
  return domains
    .map((d) => symbolHtml(DOMAIN_SYMBOL[d] ?? '') ?? d)
    .join(' ');
}


/**
 * The card types, as the icon font draws them.
 *
 * These are the glyphs the printed frames put in the type bar, and they are
 * the half of the font that rules text never asks for -- `SYMBOLS` is keyed by
 * the shorthand that appears *in* card text, and no card writes "[U]".
 *
 * `rune` and `token` are deliberately absent: neither has a glyph here, and a
 * missing icon is a word with no picture, while a guessed one is the wrong
 * picture. `signatureSpell` shares the spell mark, because that is what the
 * printed card shows.
 */
export const TYPE_SYMBOL: Record<string, string> = {
  unit: 'U',
  champion: 'H',
  spell: 'S',
  signatureSpell: 'S',
  gear: 'G',
  legend: 'L',
  battlefield: 'B',
};

/** A card type's icon, or nothing for the types the font does not draw. */
export function typeHtml(type: string): string {
  const glyph = TYPE_SYMBOL[type];
  return glyph ? `<i class="sym sym-type">${glyph}</i>` : '';
}


/** A domain's glyph and its colour class, for callers building their own markup. */
export function domainRune(domain: string): { glyph: string; kind: string } | null {
  const token = DOMAIN_SYMBOL[domain];
  const found = token ? SYMBOLS[token] : undefined;
  return found ? { glyph: found.glyph, kind: found.kind } : null;
}


/** One symbol's glyph and colour class, for callers building their own markup. */
export function symbolParts(token: string): { glyph: string; kind: string } | null {
  const found = SYMBOLS[token];
  return found ? { glyph: found.glyph, kind: found.kind } : null;
}
